import { supabase } from '../lib/supabase';

/**
 * Search for users by exact email address
 * @param {string} searchTerm - Email address to search for
 * @returns {Promise<{data: Array, error: Error|null}>}
 */
export const searchUsers = async (searchTerm) => {
  if (!searchTerm || !searchTerm.includes('@')) {
    return { data: [], error: null };
  }

  try {
    const { data, error } = await supabase.rpc('search_users', {
      search_term: searchTerm,
    });

    if (error) throw error;
    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error searching users:', error);
    return { data: [], error };
  }
};

/**
 * Send a friend request
 * @param {string} addresseeId - UUID of user to send request to
 * @param {string} [message] - Optional message (max 200 chars)
 * @returns {Promise<{data: object|null, autoAccepted: boolean, error: Error|null}>}
 */
export const sendFriendRequest = async (addresseeId, message = null) => {
  try {
    const { data, error } = await supabase.rpc('send_friend_request', {
      p_addressee_id: addresseeId,
      p_message: message?.substring(0, 200) || null,
    });

    // The mutual-request auto-accept path can trip the friend-capacity
    // trigger; surface that as a regular error so callers can paywall.
    if (error) throw error;

    if (!data.success) {
      throw new Error(data.error);
    }

    return { data: data.data, autoAccepted: !!data.auto_accepted, error: null };
  } catch (error) {
    console.error('Error sending friend request:', error);
    return { data: null, autoAccepted: false, error };
  }
};

/**
 * Respond to a friend request
 * @param {string} friendshipId - UUID of the friendship record
 * @param {'accepted'|'declined'} status - Response status
 * @returns {Promise<{data: object|null, error: Error|null}>}
 */
export const respondToFriendRequest = async (friendshipId, status) => {
  try {
    const { data, error } = await supabase.rpc('respond_to_friend_request', {
      p_friendship_id: friendshipId,
      p_status: status,
    });

    if (error) throw error;

    if (!data.success) {
      throw new Error(data.error);
    }

    return { data: data.data, error: null };
  } catch (error) {
    console.error('Error responding to friend request:', error);
    return { data: null, error };
  }
};

/**
 * Remove a friend
 * @param {string} friendUserId - UUID of friend to remove
 * @returns {Promise<{success: boolean, error: Error|null}>}
 */
export const removeFriend = async (friendUserId) => {
  try {
    const { data, error } = await supabase.rpc('remove_friend', {
      p_friend_user_id: friendUserId,
    });

    if (error) throw error;

    if (!data.success) {
      throw new Error(data.error);
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Error removing friend:', error);
    return { success: false, error };
  }
};

/**
 * Cancel a pending outgoing friend request.
 * Only succeeds when the current user is the requester and the row is still pending.
 * @param {string} friendshipId - UUID of the friendship record
 * @returns {Promise<{success: boolean, error: Error|null}>}
 */
export const cancelFriendRequest = async (friendshipId) => {
  try {
    const { data, error } = await supabase.rpc('cancel_friend_request', {
      p_friendship_id: friendshipId,
    });

    if (error) throw error;

    if (!data.success) {
      throw new Error(data.error);
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Error cancelling friend request:', error);
    return { success: false, error };
  }
};

/**
 * Look up the friendship (if any) between the current user and another user.
 * Returns null when no row exists; otherwise returns the friendship id, status,
 * and which direction it goes relative to the caller ("outgoing" if the caller
 * is the requester, "incoming" if the caller is the addressee).
 * @param {string} otherUserId - UUID of the other user
 * @returns {Promise<{data: {friendshipId: string, status: string, direction: 'outgoing'|'incoming'}|null, error: Error|null}>}
 */
export const getFriendshipWith = async (otherUserId) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { data: null, error: new Error('Not authenticated') };

    const { data, error } = await supabase
      .from('friendships')
      .select('id, requester_id, addressee_id, status')
      .or(`and(requester_id.eq.${user.id},addressee_id.eq.${otherUserId}),and(requester_id.eq.${otherUserId},addressee_id.eq.${user.id})`)
      .maybeSingle();

    if (error) throw error;
    if (!data) return { data: null, error: null };

    return {
      data: {
        friendshipId: data.id,
        status: data.status,
        direction: data.requester_id === user.id ? 'outgoing' : 'incoming',
      },
      error: null,
    };
  } catch (error) {
    console.error('Error fetching friendship status:', error);
    return { data: null, error };
  }
};

/**
 * Get user's friends list
 * @returns {Promise<{data: Array, error: Error|null}>}
 */
export const getFriends = async () => {
  try {
    const { data, error } = await supabase.rpc('get_friends');
    if (error) throw error;
    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error fetching friends:', error);
    return { data: [], error };
  }
};

/**
 * Get pending friend requests
 * @param {'incoming'|'outgoing'} direction
 * @returns {Promise<{data: Array, error: Error|null}>}
 */
export const getFriendRequests = async (direction = 'incoming') => {
  try {
    const { data, error } = await supabase.rpc('get_friend_requests', {
      p_direction: direction,
    });
    if (error) throw error;
    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error fetching friend requests:', error);
    return { data: [], error };
  }
};

/**
 * Get count of pending incoming friend requests (for badge)
 * @returns {Promise<number>}
 */
export const getPendingRequestCount = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return 0;

    const { count, error } = await supabase
      .from('friendships')
      .select('id', { count: 'exact', head: true })
      .eq('addressee_id', user.id)
      .eq('status', 'pending');

    if (error) throw error;
    return count || 0;
  } catch (error) {
    console.error('Error fetching pending request count:', error);
    return 0;
  }
};
